// ---------------------------------------------------------------------------
// Creator default design-system binding (G2 — 2026-07-27 design doc §2.3)
//
// RC-B: the creator hero-chat path never sets conversation.designSystemPreset
// (its setters are developer-only), so plain chat generations ran with a null
// preset — structurally unable to follow any design system, and
// studio_projects.design_system_preset_id stayed null (live defect c890b8e8).
//
// This module closes the unbound path:
//  - `resolveCreatorDefaultDesignSystemBinding` resolves the design-system
//    catalog's default entry (DEFAULT_DESIGN_SYSTEM_STATE — no token values
//    are hardcoded here) and binds it to a real `design_system_presets` row
//    named "Cogo Default" (reused when present, created via the existing
//    save-preset path when absent) so the preset uuid can be persisted to
//    `studio_projects.design_system_preset_id` at project creation. Token
//    resolution NEVER fails (the catalog default is a constant); row-id
//    resolution degrades to null on infra failure — persisted only when real.
//  - `assertCreatorGenerationDesignSystemAuthority` is the fail-closed
//    guard: a non-library generation with no design system (and no other
//    design authority) must throw visibly, never proceed unbound.
// ---------------------------------------------------------------------------

import { isSupabaseConfigured } from '@/lib/supabase'
import {
  BASE_COLOR_OPTIONS,
  CHART_OPTIONS,
  DEFAULT_DESIGN_SYSTEM_STATE,
  FONT_OPTIONS,
  RADIUS_OPTIONS,
  STYLE_OPTIONS,
  THEME_OPTIONS,
  type DesignSystemState,
} from '@/features/design-system/lib/design-system-options'

export const CREATOR_DEFAULT_DESIGN_SYSTEM_PRESET_NAME = 'Cogo Default'

export type CreatorDefaultDesignSystemOrigin =
  | 'existing-default-preset'
  | 'created-default-preset'
  /** Tokens bound from the catalog constant, but no server preset row could
   * be resolved (signed-out user or preset-catalog infra failure). The
   * design system STILL binds; only the uuid column stays unset. */
  | 'catalog-default-only'

export interface CreatorDefaultDesignSystemBinding {
  preset: DesignSystemState
  presetId: string | null
  origin: CreatorDefaultDesignSystemOrigin
}

interface FetchPresetsLike {
  presets: Array<{ id: string; name: string; tokens?: unknown; createdAt?: string }>
  error?: string
}

interface SavePresetResultLike {
  success: boolean
  error?: string
  data?: { id: string; name: string }
}

export interface ResolveCreatorDefaultDesignSystemDeps {
  fetchPresets?: (userId: string) => Promise<FetchPresetsLike>
  savePreset?: (params: {
    userId: string
    name: string
    description: string
    designSystem: DesignSystemState
  }) => Promise<SavePresetResultLike>
  /** Override the preset-catalog availability probe (defaults to
   * isSupabaseConfigured, or true when a fetcher is injected). */
  presetCatalogAvailable?: boolean
}

function inOptions(
  candidate: unknown,
  options: ReadonlyArray<{ value: string }>,
): candidate is string {
  return (
    typeof candidate === 'string' &&
    options.some((option) => option.value === candidate)
  )
}

/** Structural 6-axis validation mirroring fetch-presets-via-worker. */
function parseDesignSystemState(value: unknown): DesignSystemState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const state = value as Record<string, unknown>
  if (
    !inOptions(state.style, STYLE_OPTIONS) ||
    !inOptions(state.baseColor, BASE_COLOR_OPTIONS) ||
    !inOptions(state.theme, THEME_OPTIONS) ||
    !inOptions(state.chart, CHART_OPTIONS) ||
    !inOptions(state.radius, RADIUS_OPTIONS) ||
    !inOptions(state.font, FONT_OPTIONS)
  ) {
    return null
  }
  return {
    style: state.style,
    baseColor: state.baseColor,
    theme: state.theme,
    chart: state.chart,
    radius: state.radius,
    font: state.font,
  } as DesignSystemState
}

const CATALOG_DEFAULT_ONLY: CreatorDefaultDesignSystemBinding = {
  preset: DEFAULT_DESIGN_SYSTEM_STATE,
  presetId: null,
  origin: 'catalog-default-only',
}

/**
 * Resolve the default design system for a creator chat generation.
 * Never throws and never resolves a null preset — the guard below stays a
 * structural backstop rather than a user-facing failure mode.
 */
export async function resolveCreatorDefaultDesignSystemBinding(
  userId: string | null | undefined,
  deps: ResolveCreatorDefaultDesignSystemDeps = {},
): Promise<CreatorDefaultDesignSystemBinding> {
  if (!userId) return CATALOG_DEFAULT_ONLY
  // Unconfigured Supabase (the appliance bundle): the preset catalog cannot
  // exist, so degrade to the catalog default immediately instead of paying a
  // failed round-trip. An injected fetcher counts as an available catalog so
  // tests and future non-Supabase catalogs keep working.
  const catalogAvailable =
    deps.presetCatalogAvailable ?? (deps.fetchPresets ? true : isSupabaseConfigured)
  if (!catalogAvailable) return CATALOG_DEFAULT_ONLY
  try {
    const fetchPresets =
      deps.fetchPresets ??
      (await import('@/features/design-system/lib/fetch-presets'))
        .fetchDesignSystemPresets
    const { presets, error } = await fetchPresets(userId)
    if (error) {
      console.warn(
        '[creator-design-system-binding] preset catalog unavailable:',
        error,
      )
      return CATALOG_DEFAULT_ONLY
    }
    const targetName =
      CREATOR_DEFAULT_DESIGN_SYSTEM_PRESET_NAME.toLocaleLowerCase()
    // F4 (adversarial review) — ACCEPTED RESIDUAL: this name-keyed
    // reuse-or-create runs without a lock, so two concurrent first
    // generations can both miss the lookup and both create a "Cogo Default"
    // row, and a user rename of the row forks the key (the next generation
    // recreates it). Impact is RLS-scoped row hygiene ONLY: a duplicate or
    // extra preset row in the user's own catalog — no cross-user leak, and
    // the generation still binds a valid preset either way. No locking is
    // built for that; instead the lookup prefers the OLDEST matching row
    // (the catalog fetch returns newest-first) so every later resolution
    // converges on the same row id and duplicates go inert.
    const named = (presets ?? [])
      .filter((preset) => preset.name?.trim().toLocaleLowerCase() === targetName)
      // ISO-8601 sorts lexicographically; rows without createdAt sort last
      // so a dated row always wins the convergence pick.
      .sort((a, b) =>
        (a.createdAt ?? '￿').localeCompare(b.createdAt ?? '￿'),
      )[0]
    if (named) {
      const tokens = parseDesignSystemState(named.tokens)
      if (tokens) {
        return {
          preset: tokens,
          presetId: named.id,
          origin: 'existing-default-preset',
        }
      }
      // A row whose tokens diverge from the 6-axis schema must not be bound
      // by id while the generation context uses different (catalog) tokens.
      console.warn(
        '[creator-design-system-binding] "Cogo Default" row has malformed tokens; using catalog default without an id binding',
      )
      return CATALOG_DEFAULT_ONLY
    }
    const savePreset =
      deps.savePreset ??
      (await import('@/features/design-system/lib/save-preset'))
        .saveDesignSystemPreset
    const saved = await savePreset({
      userId,
      name: CREATOR_DEFAULT_DESIGN_SYSTEM_PRESET_NAME,
      description:
        'Default design system automatically applied to chat-generated apps.',
      designSystem: DEFAULT_DESIGN_SYSTEM_STATE,
    })
    if (saved.success && saved.data?.id) {
      return {
        preset: DEFAULT_DESIGN_SYSTEM_STATE,
        presetId: saved.data.id,
        origin: 'created-default-preset',
      }
    }
    console.warn(
      '[creator-design-system-binding] could not create the default preset row:',
      saved.error,
    )
    return CATALOG_DEFAULT_ONLY
  } catch (err) {
    console.warn(
      '[creator-design-system-binding] default preset resolution failed:',
      err,
    )
    return CATALOG_DEFAULT_ONLY
  }
}

export class CreatorGenerationDesignSystemError extends Error {
  readonly code = 'creator_generation_design_system_missing' as const

  constructor() {
    super(
      'App generation is blocked: no design system is bound for this request. Chat-generated apps must always carry a design system (default preset resolution failed).',
    )
    this.name = 'CreatorGenerationDesignSystemError'
  }
}

/**
 * Fail-closed boundary (design doc §2.3): the NON-library generation path
 * must never proceed with no design system. Library bindings, OpenPencil
 * source workspaces/contexts, server-owned outcome (SKU) generations, and a
 * verified Design System pack release binding (F5) carry their own design
 * authority and are exempt.
 */
export function assertCreatorGenerationDesignSystemAuthority(input: {
  designSystemPreset: DesignSystemState | null | undefined
  hasLibraryDesignAuthority: boolean
  hasSourceDesignAuthority: boolean
  hasServerOwnedOutcomeAuthority: boolean
  /** F5 — a hash-verified pack release binding is a full design authority. */
  hasPackReleaseAuthority?: boolean
}): void {
  if (
    input.hasLibraryDesignAuthority ||
    input.hasSourceDesignAuthority ||
    input.hasServerOwnedOutcomeAuthority ||
    input.hasPackReleaseAuthority === true
  ) {
    return
  }
  if (!input.designSystemPreset) {
    throw new CreatorGenerationDesignSystemError()
  }
}
